import { useNavigate } from "react-router";
import { Fragment, SyntheticEvent, useMemo, useState } from "react";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import {
	EmailLabelText,
	NameLabelText,
	PersonSearchNoResultsFound,
	PriceFormatList,
	PriceFormatNet,
	PriceFormatNetWithMultiplier,
	PricingFormatLabelText,
	SearchPeopleInputText
} from "components/Preferences/text.ts";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { models } from "types/api/viewModels.ts";
import {
	AdminSearchCheckYourSpellingBullet,
	AdminSearchNoResultsFoundSubheader,
	AdminSearchUserBullet,
	ContactCareForSearchHelpText,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import ChevronRight from "@mui/icons-material/ChevronRight";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import {
	PeopleSettingsTableHeader,
	PeopleSettingsTableNoResultsFound,
	PeopleTableRow
} from "components/Preferences/PeopleSettings/PeopleSettingsTable.styles.ts";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch.tsx";
import { TableBody } from "@mui/material";
import { formatPersonName } from "components/Preferences/preferencesUtils.ts";
import { AcknowledgementPriceFormat } from "types/declarations/enums.ts";

interface PeopleSettingsTableProps {
	people?: models["PersonPermissionsViewModel"][] | null;
	isLoading: boolean;
}

const PeopleSettingsTable = ({ people, isLoading }: PeopleSettingsTableProps) => {
	const navigate = useNavigate();
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const AcknowledgementPriceFormatDescription = {
		[AcknowledgementPriceFormat.NET]: PriceFormatNet,
		[AcknowledgementPriceFormat.LIST]: PriceFormatList,
		[AcknowledgementPriceFormat.NET_MULTIPLIER]: PriceFormatNetWithMultiplier
	};

	const getPriceFormatDescription = (priceFormat: string | undefined) => {
		return priceFormat ? AcknowledgementPriceFormatDescription[priceFormat] : UnavailableDataPlaceholderText;
	};

	const getPeopleNames = (peopleList: models["PersonPermissionsViewModel"][] | undefined | null) => {
		return peopleList?.map((person) => {
			const firstName = person.firstName ?? "";
			const lastName = person.lastName ?? "";
			return `${firstName} ${lastName}`.trim();
		});
	};

	const getEmails = (peopleList: models["PersonPermissionsViewModel"][] | undefined | null) => {
		return peopleList?.map((person) => person.email ?? "");
	};

	const searchOptions = useMemo(() => {
		const names = getPeopleNames(people) ?? [];
		const emails = getEmails(people) ?? [];

		return Array.from(new Set([...names, ...emails]));
	}, [people]);
	const baseTableHeadCells = useMemo(
		() =>
			[
				{ id: "name", label: NameLabelText, sortable: true },
				{ id: "email", label: EmailLabelText, sortable: true },
				{ id: "pricingFormat", label: PricingFormatLabelText },
				{ id: "open", label: "" }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable ?? false
			})) satisfies HeadCell[],
		[]
	);
	const filteredHeadCells = useMemo(() => baseTableHeadCells, [baseTableHeadCells]);

	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("name", "asc");
	const sortedPeople = useMemo(() => {
		if (!people) return [];

		const peopleCopy = people.slice();

		// Custom comparator for "name" field since it doesn't exist on the model
		if (orderBy === "name") {
			return peopleCopy.sort((a, b) => {
				const nameA = formatPersonName(a).toLowerCase();
				const nameB = formatPersonName(b).toLowerCase();

				if (order === "asc") {
					return nameA.localeCompare(nameB);
				} else {
					return nameB.localeCompare(nameA);
				}
			});
		}

		// Use default comparator for other fields
		return peopleCopy.sort(getComparator(order, orderBy));
	}, [people, getComparator, order, orderBy]);
	const filteredPeople = useMemo(() => {
		let filtered = sortedPeople;
		if (searchValue && searchValue.trim() !== "") {
			const search = searchValue.trim().toLowerCase();
			filtered = filtered.filter(
				(person) =>
					person?.email?.toLowerCase().includes(search) ||
					formatPersonName(person).toLowerCase().includes(search)
			);
		}
		return filtered;
	}, [searchValue, sortedPeople]);

	const getTableCells = (person: models["PersonPermissionsViewModel"]) => {
		const tableCells = [
			{
				testId: "people-settings-table-name",
				props: { fixedWidth: "250px" },
				content: formatPersonName(person) ?? UnavailableDataPlaceholderText
			},
			{
				testId: "people-settings-table-email",
				props: { fixedWidth: "250px" },
				content: person?.email ?? UnavailableDataPlaceholderText
			},
			{
				testId: "people-settings-table-pricing-format",
				props: { fixedWidth: "20px" },
				content: getPriceFormatDescription(person.acknowledgementPriceFormat)
			}
		];

		return (
			<Fragment key={person.userId}>
				{tableCells.map(({ testId, props, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
						{...props}
					>
						{content}
					</StyledTableCell>
				))}

				<StyledTableCell
					fixedWidth="40px"
					isLastIndex
				>
					<ChevronRight />
				</StyledTableCell>
			</Fragment>
		);
	};

	const handlePeopleSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values ?? "");
	};

	const getTableContent = () => {
		return (
			<>
				<PeopleSettingsTableHeader>
					<SuggestedSearch
						ariaLabel="people-settings-search-results"
						dataTestId="people-settings-search-results"
						dropDownOpenOnChar={3}
						placeholder={SearchPeopleInputText}
						handleSearch={(event, values) => handlePeopleSearch(event, values ?? "")}
						id="people-settings-search-results"
						searchOptions={searchOptions ?? []}
						searchValue={searchValue ?? ""}
						size="small"
					/>
				</PeopleSettingsTableHeader>
				{filteredPeople && filteredPeople.length > 0 ? (
					<StyledTableWrapper>
						<SortableTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							headCells={filteredHeadCells}
						/>
						<TableBody>
							{filteredPeople.map((person: models["PersonPermissionsViewModel"]) => (
								<PeopleTableRow
									key={person.email}
									data-testid={`people-settings-${person.email}`}
									onClick={() => navigate(`${person.userId}`)}
								>
									{getTableCells(person)}
								</PeopleTableRow>
							))}
						</TableBody>
					</StyledTableWrapper>
				) : (
					<PeopleSettingsTableNoResultsFound>
						<img
							src="/assets/NoOrdersSadBoxImage.svg"
							alt="No Results Found"
							data-testid="person-search-no-results-found"
						/>
						<h1>{PersonSearchNoResultsFound}</h1>
						<div>
							<div>{AdminSearchNoResultsFoundSubheader}</div>
							<ul>
								<li>{AdminSearchUserBullet}</li>
								<li>{AdminSearchCheckYourSpellingBullet}</li>
							</ul>
						</div>
						<div>{ContactCareForSearchHelpText}</div>
					</PeopleSettingsTableNoResultsFound>
				)}
			</>
		);
	};

	return <>{isLoading ? <TableSkeleton /> : getTableContent()}</>;
};

export default PeopleSettingsTable;
